import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, PlayCircle } from 'lucide-react';
import { ProgressBar } from './components/ui/ProgressBar';
import { SlideTitle } from './components/SlideTitle';
import { SlideLifecycle } from './components/SlideLifecycle';
import { SlideMarketSize } from './components/SlideMarketSize';
import { SlideCalculator } from './components/SlideCalculator';
import { SlideAIEconomics } from './components/SlideAIEconomics';
import { SlideNewCategory } from './components/SlideNewCategory';
import { SlideStrategy } from './components/SlideStrategy';
import { SlideWhatSnakkerDoes } from './components/SlideWhatSnakkerDoes';
import { SlideCampaignExample } from './components/SlideCampaignExample';
import { SlideFastCalculator } from './components/SlideFastCalculator';
import { SlideCTA } from './components/SlideCTA';
import { SlidePressure } from './components/SlidePressure';
import { SlideLocalRelevance } from './components/SlideLocalRelevance';
import { SlideScaleVsRelevance } from './components/SlideScaleVsRelevance';
import { SlideCentralizeToLocalize } from './components/SlideCentralizeToLocalize';
import { SlideCampaignEngine } from './components/SlideCampaignEngine';
import { SlideControlTower } from './components/SlideControlTower';
import { SlideProposition } from './components/SlideProposition';
import { SlideObjections } from './components/SlideObjections';
import { ExecutiveSummary } from './components/ExecutiveSummary';
import { SnakPlayer } from './components/SnakPlayer';
export function App() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(1);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [snakOpen, setSnakOpen] = useState(false);
  const totalSlides = 19;
  const nextSlide = useCallback(() => {
    if (currentSlide < totalSlides - 1) {
      setDirection(1);
      setCurrentSlide(prev => prev + 1);
    }
  }, [currentSlide, totalSlides]);
  const prevSlide = useCallback(() => {
    if (currentSlide > 0) {
      setDirection(-1);
      setCurrentSlide(prev => prev - 1);
    }
  }, [currentSlide]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Space') nextSlide();else if (e.key === 'ArrowLeft') prevSlide();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };
  const renderSlide = () => {
    switch (currentSlide) {
      case 0:
        return <SlideTitle onContinue={nextSlide} data-id="element-0" data-mp="2sv0p5del46um" />;
      case 1:
        return <SlidePressure onContinue={nextSlide} data-id="element-1" data-mp="18e17fdz2bcgv" />;
      case 2:
        return <SlideLifecycle onContinue={nextSlide} data-id="element-2" data-mp="18bolyqu3ijz2" />;
      case 3:
        return <SlideMarketSize onContinue={nextSlide} data-id="element-3" data-mp="1d59hmc87v1v3" />;
      case 4:
        return <SlideCalculator onContinue={nextSlide} data-id="element-4" data-mp="ygd25u3tymkg" />;
      case 5:
        return <SlideLocalRelevance onContinue={nextSlide} data-id="element-5" data-mp="13eocs71y913q" />;
      case 6:
        return <SlideScaleVsRelevance onContinue={nextSlide} data-id="element-6" data-mp="13ey7g7xxvwfr" />;
      case 7:
        return <SlideNewCategory onContinue={nextSlide} data-id="element-7" data-mp="1r5kb7n0wuujy" />;
      case 8:
        return <SlideProposition onContinue={nextSlide} data-id="element-8" data-mp="1w3luo9290vvy" />;
      case 9:
        return <SlideAIEconomics onContinue={nextSlide} data-id="element-9" data-mp="1i3kvcy1mtwil" />;
      case 10:
        return <SlideCentralizeToLocalize onContinue={nextSlide} data-id="element-10" data-mp="1i3b4l88rjlbu" />;
      case 11:
        return <SlideCampaignEngine onContinue={nextSlide} data-id="element-11" data-mp="1m3l7gc19dvmh" />;
      case 12:
        return <SlideStrategy onContinue={nextSlide} data-id="element-12" data-mp="6o5vnmi1gqjs" />;
      case 13:
        return <SlideControlTower onContinue={nextSlide} data-id="element-13" data-mp="aq0v0lgestux" />;
      case 14:
        return <SlideWhatSnakkerDoes onContinue={nextSlide} data-id="element-14" data-mp="anwixg8cwojk" />;
      case 15:
        return <SlideCampaignExample onContinue={nextSlide} data-id="element-15" data-mp="qyqavo0wi78c" />;
      case 16:
        return <SlideFastCalculator onContinue={nextSlide} data-id="element-16" data-mp="m65mx34oeocq" />;
      case 17:
        return <SlideObjections onContinue={nextSlide} data-id="element-17" data-mp="h72npzbdncco" />;
      case 18:
        return <SlideCTA onContinue={nextSlide} data-id="element-18" data-mp="h59dyb5gwc0g" />;
      default:
        return null;
    }
  };
  return <div className="w-full h-screen bg-navy-800 overflow-hidden relative font-sans text-slate-900" data-id="element-19" data-mp="c66ct1slypxr">
      <ProgressBar current={currentSlide} total={totalSlides} onBack={prevSlide} onSkip={nextSlide} data-id="element-20" data-mp="19tx5gogt7xmg" />

      <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-21" data-mp="zxal00seb8ky">
        <motion.div key={currentSlide} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
        x: {
          type: 'spring',
          stiffness: 300,
          damping: 30
        },
        opacity: {
          duration: 0.2
        }
      }} className="absolute inset-0 w-full h-full" data-id="element-22" data-mp="vw8nppi1h6lm">
          {renderSlide()}
        </motion.div>
      </AnimatePresence>

      <button onClick={() => setSummaryOpen(true)} className="fixed bottom-6 left-6 z-40 flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#6C2BD9] text-sm font-semibold shadow-lg shadow-[#6C2BD9]/30 hover:bg-[#5A23B8] hover:shadow-xl hover:shadow-[#6C2BD9]/40 hover:-translate-y-0.5 transition-all" aria-label="Open executive summary" style={{
      color: '#ffffff'
    }} data-id="element-23" data-mp="25422d2mqo5cy">
        <Sparkles className="w-4 h-4" style={{
        color: '#ffffff'
      }} data-id="element-24" data-mp="2nwe2rjtu0wkg" />
        <span style={{
        color: '#ffffff'
      }} data-id="element-25" data-mp="2ssbj3q53i2x8">
          Executive Summary
        </span>
      </button>

      <button onClick={() => setSnakOpen(true)} className="fixed bottom-6 left-[15.5rem] z-40 flex items-center gap-2 px-4 py-2.5 rounded-full bg-navy-900 border border-[#6C2BD9]/30 text-sm font-semibold text-[#6C2BD9] shadow-lg shadow-slate-900/5 hover:bg-[#6C2BD9]/[0.06] hover:-translate-y-0.5 transition-all" aria-label="Play an example Snak" data-id="element-26" data-mp="yhptvskg9wr6">
        <PlayCircle className="w-4 h-4" data-id="element-27" data-mp="25snu46azns88" />
        <span data-id="element-28" data-mp="25rma3cbzvved">Play a Snak</span>
      </button>

      <ExecutiveSummary open={summaryOpen} onClose={() => setSummaryOpen(false)} data-id="element-29" data-mp="2kq6k1bzekbh9" />

      <SnakPlayer open={snakOpen} onClose={() => setSnakOpen(false)} data-id="element-30" data-mp="2frv7gthq3mvc" />
    </div>;
}